import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/base/SectionHeading";
import { principles, siteInfo, siteStats, testimonials } from "@/mocks/site";
import { categories } from "@/mocks/categories";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah7gc3oh653ivfvnn8g";

export default function About() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: `关于 ${siteInfo.name}`,
        description: siteInfo.description,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: `联系 ${siteInfo.name}`,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "/about" },
        ],
      },
    ],
    []
  );

  usePageMeta({
    title: "关于我们｜瓜线内容原则与投稿联系",
    description:
      "瓜线专注热门吃瓜来龙去脉整理，坚持按时间线梳理、理清人物关系、标注求证状态。了解我们的内容原则，或通过表单投稿纠错、联系编辑团队。",
    keywords: "瓜线,关于我们,吃瓜内容原则,投稿纠错,热门吃瓜团队",
    path: "/about",
    jsonLd,
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("已收到你的信息，编辑会尽快核对。");
      return;
    }
    formData.delete("website_alt");

    setStatus("loading");
    setMessage("");
    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, String(value)));

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("已收到你的信息，编辑会尽快核对。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMessage || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络不太稳定，请稍后再试一次。");
    }
  };

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100 px-4 md:px-8 py-10 md:py-14">
        <div className="w-full">
          <Breadcrumbs items={[{ name: "首页", path: "/" }, { name: "关于我们" }]} />
          <div className="mt-5 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-primary-600">
              <span className="w-6 h-px bg-primary-500" />
              关于瓜线
            </span>
            <h1 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold leading-tight text-foreground-950">
              不想再让你吃残瓜
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              {siteInfo.description}我们相信，热点事件真正难的不是「知道」，而是「看懂」——
              看懂它从哪来、为什么反转、最后又走向了哪里。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-6 md:grid-cols-4">
            {siteStats.map((stat) => (
              <div key={stat.label}>
                <div className="font-heading text-xl md:text-2xl font-bold text-primary-600">
                  {stat.value}
                </div>
                <div className="mt-1 text-xs text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeading
              kicker="网站定位"
              title="做热点的整理者，不做情绪的放大器"
              desc="信息爆炸的时代，缺的从来不是爆料，而是一份能反复对照、经得起回头看的整理。"
            />
            <div className="mt-6 space-y-4 text-sm leading-loose text-foreground-600">
              <p>
                瓜线从 {siteInfo.since} 年开始做同一件事：把散落在各个平台的信息，按照时间顺序重新拼起来。
                我们关注的不只是「发生了什么」，更是「它是怎么一步步变成这样的」。
              </p>
              <p>
                我们覆盖{categories.map((c) => c.name).join("、")}等领域。不同圈层的热点逻辑差很多，
                因此每份事件档案都会把背景、人物、节点、结论分开写清楚，方便你按需阅读。
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {principles.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-xl">
                    <i className={item.icon} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
        <SectionHeading
          align="center"
          kicker="用户口碑"
          title="他们这样用瓜线"
          desc="有人用来通勤补课，有人用来核对传闻，也有人只是不想在群里插不上话。"
        />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials.map((item) => (
            <figure key={item.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <div className="flex items-center gap-1 text-accent-500">
                {[0, 1, 2, 3, 4].map((s) => (
                  <i key={s} className="ri-star-fill text-sm" />
                ))}
              </div>
              <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">
                {item.text}
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <img
                  src={item.avatar}
                  alt={`${item.name} 的头像`}
                  title={`${item.name} - 瓜线用户`}
                  className="w-10 h-10 rounded-full object-cover object-top"
                />
                <span>
                  <span className="block text-sm font-semibold text-foreground-900">
                    {item.name}
                  </span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section id="contact" className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50 scroll-mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeading
              kicker="投稿与联系"
              title="有线索、有疑问，都可以告诉我们"
              desc="无论是指出时间线错误，还是提供新的进展线索，我们都会认真核对。"
            />
            <ul className="mt-6 space-y-4 text-sm text-foreground-600">
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-mail-line" />
                </span>
                <span>
                  <span className="block font-semibold text-foreground-900">内容纠错</span>
                  <span className="break-all text-foreground-600">{siteInfo.email}</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
                  <i className="ri-lightbulb-line" />
                </span>
                <span>
                  <span className="block font-semibold text-foreground-900">投稿爆料</span>
                  <span className="break-all text-foreground-600">{siteInfo.submitEmail}</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-accent-50 text-accent-600">
                  <i className="ri-time-line" />
                </span>
                <span>
                  <span className="block font-semibold text-foreground-900">处理时效</span>
                  <span className="text-foreground-600">一般在 1-2 个工作日内完成核对</span>
                </span>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="gualine-contact-form"
              className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7"
            >
              <div className="gossip-field-shield" aria-hidden="true">
                <label htmlFor="contact-website-alt">公司网址备用</label>
                <input
                  id="contact-website-alt"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700">
                    称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="方便回复的邮箱"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-type" className="block text-xs font-medium text-foreground-700">
                  事项类型
                </label>
                <select
                  id="contact-type"
                  name="type"
                  required
                  defaultValue="内容纠错"
                  className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                >
                  <option value="内容纠错">内容纠错</option>
                  <option value="提供线索">提供线索</option>
                  <option value="转载授权">转载授权</option>
                  <option value="合作咨询">合作咨询</option>
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700">
                  具体内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请尽量写清事件名称、涉及节点与你的判断依据（最多 500 字）"
                  className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-foreground-500">
                  我们不会公开你的联系方式，请放心填写。
                </p>
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-send-plane-line" />
                  </span>
                  {status === "loading" ? "提交中" : "提交信息"}
                </button>
              </div>

              {message && (
                <p
                  className={`mt-3 text-xs ${
                    status === "error" ? "text-primary-700" : "text-secondary-700"
                  }`}
                  role="status"
                >
                  {message}
                </p>
              )}
            </form>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to="/events"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors duration-200 hover:bg-background-100 cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-compass-3-line" />
                </span>
                去逛事件库
              </Link>
              <Link
                to="/#subscribe"
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-primary-600 transition-colors duration-200 hover:text-primary-700 cursor-pointer"
              >
                订阅时间线更新
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}